import { SchemaValidationError, StrictUnion } from '@helpers';
import wretch, { Wretch } from 'wretch';
import { WretchError } from 'wretch/resolver';
import { ZodSchema } from 'zod';
import { languageTags } from './languageTags';
import { validateResponseSchema } from './validateResponseSchema';

export type ApiFetchDefaults = {
  basePath?: string;
  token?: string;
};

let apiFetchDefaults: ApiFetchDefaults = { basePath: '/' };

export const configureApiFetch = (config: ApiFetchDefaults) => {
  apiFetchDefaults = { ...apiFetchDefaults, ...config };
};

const getApiFetchDefaults = (): Readonly<ApiFetchDefaults> => apiFetchDefaults;

type HttpMethod = 'get' | 'post' | 'delete' | 'put';

export type ApiFetchOptions<T> = {
  basePath?: string;
  method?: HttpMethod;
  body?: object | string;
  noToken?: boolean;
  token?: string;
  schema?: ZodSchema<T>;
  language?: string;
  headers?: Record<string, string>;
  options?: Record<string, string | AbortSignal | undefined>;
  signal?: AbortSignal;
};

type HandleRequestMethodProps = {
  method: HttpMethod;
  request: Wretch<unknown, unknown, undefined>;
  url: string;
  body?: object | string;
};

const handleRequestMethod = ({
  method,
  request,
  url,
  body,
}: HandleRequestMethodProps) => {
  if (method === 'post') {
    return request.post(body, url);
  }

  if (method === 'put') {
    return request.put(body, url);
  }

  if (method === 'delete') {
    return request.delete(url);
  }

  return request.get(url);
};

export type ApiFetchResponse<T> = StrictUnion<
  | {
      validationError: SchemaValidationError | false;
      data: T;
    }
  | {
      error: WretchError;
      validationError: false;
    }
>;

export const apiFetch = async <T>(
  url: string,
  options: ApiFetchOptions<T> = {}
): Promise<ApiFetchResponse<T>> => {
  try {
    const data = await wretchRequest(url, options);

    return {
      data,
      validationError: false,
    };
  } catch (error) {
    if (error instanceof WretchError) {
      return { error, validationError: false };
    }

    return { error: new WretchError(), validationError: false };
  }
};

export const wretchRequest = async <T>(
  url: string,
  options: ApiFetchOptions<T> = {}
): Promise<T> => {
  const { basePath: defaultBasePath, token: defaultToken } =
    getApiFetchDefaults();
  const basePath = options.basePath ?? defaultBasePath ?? '/';
  const token = options.token ?? defaultToken;
  const method = options.method ?? 'get';
  const language = options.language ?? 'en';

  const request = (() => {
    const wretchInstance = wretch(basePath, {
      mode: 'cors',
      headers: {
        'X-Request-ID': crypto.randomUUID(),
        'Germ-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        'Accept-Language': languageTags[language] || language || 'en-US',
        ...options.headers,
      },
      ...options.options,
    });

    if (options.noToken) {
      return wretchInstance;
    }

    return token ? wretchInstance.auth(`Bearer ${token}`) : wretchInstance;
  })();

  return await handleRequestMethod({
    request,
    url,
    method,
    body: options.body,
  })
    .res(response => {
      const contentType = response.headers.get('content-type');

      if (contentType === null) {
        return response;
      }

      if (contentType.includes('application/json')) {
        return response.json();
      }

      if (contentType.includes('text')) {
        return response.text();
      }

      if (
        contentType.includes('application/pdf') ||
        contentType.includes(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        )
      ) {
        return response.blob();
      }

      return response;
    })
    .then((data: T) => {
      if (options.schema) {
        const validationError = validateResponseSchema({
          data,
          endpoint: url,
          schema: options.schema,
        });

        if (validationError) {
          throw validationError;
        }
      }

      return data;
    });
};
